import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { inputClass, labelClass, inlineErrorClass, inlineSuccessClass } from '@/components/ui/inputStyles';
import { useSession } from '@/hooks/useSession';
import { formatMediaBytes, isOssStorageMode, mediaUsagePercent, type MediaStorageStatus } from '@/lib/mediaStorage';
import { cn } from '@/lib/cn';
import { aliyunLinkById, openOssAliyunGuideWindow } from '@/config/aliyunOssGuide';
import { formatOssTestFailure, type OssTestFailureDetail } from '@/lib/ossTestError';

function FieldAliyunLink({ linkId, text = '去阿里云申请/设置' }: { linkId: string; text?: string }) {
  const link = aliyunLinkById(linkId);
  if (!link) return null;
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className="ml-2 inline-flex items-center gap-0.5 text-xs font-medium text-teal-700 underline decoration-teal-600/30 underline-offset-2 hover:text-teal-800"
    >
      {text}
      <i className="fa-solid fa-arrow-up-right-from-square text-[9px] opacity-75" aria-hidden />
    </a>
  );
}

type OssConfig = {
  endpoint?: string;
  bucket?: string;
  access_key_id?: string;
  access_key_secret?: string;
  object_prefix?: string;
  region?: string;
  last_test_ok?: boolean;
};

type OssConfigResp = {
  ok: boolean;
  config?: OssConfig | null;
  media_storage?: MediaStorageStatus;
};

function normalizeOssPrefixForForm(prefix: string | undefined): string {
  const p = (prefix ?? '').trim().replace(/\/+$/, '');
  return p === '' ? 'wes' : p;
}

function formFromOssConfig(cfg: OssConfig | null | undefined): OssConfig {
  return {
    endpoint: cfg?.endpoint ?? '',
    bucket: cfg?.bucket ?? '',
    access_key_id: cfg?.access_key_id ?? '',
    access_key_secret: cfg?.access_key_secret ?? '',
    object_prefix: normalizeOssPrefixForForm(cfg?.object_prefix),
    region: cfg?.region ?? '',
    last_test_ok: cfg?.last_test_ok,
  };
}

export function OssCdnSettingsPage() {
  const { refresh: refreshSession } = useSession();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState<OssConfig>({
    endpoint: '',
    bucket: '',
    access_key_id: '',
    access_key_secret: '',
    object_prefix: 'wes',
    region: '',
  });
  const [media, setMedia] = useState<MediaStorageStatus | null>(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [testing, setTesting] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [jobId, setJobId] = useState(0);
  const [progress, setProgress] = useState({ done: 0, total: 0, status: '' });
  const [verifyOk, setVerifyOk] = useState(false);
  const [purging, setPurging] = useState(false);
  const [secretHint, setSecretHint] = useState('');

  const ossMode = media ? isOssStorageMode(media.storage_mode) : false;

  const applyServerOssState = useCallback((r: OssConfigResp, secretFallback = '') => {
    if (r.config) {
      const next = formFromOssConfig(r.config);
      if (!next.access_key_secret && secretFallback) {
        next.access_key_secret = secretFallback;
      }
      setForm(next);
      const sk = (next.access_key_secret ?? '').trim();
      if (sk) {
        setSecretHint('');
      } else if ((next.endpoint ?? '') !== '' && (next.bucket ?? '') !== '') {
        setSecretHint('未读取到 AccessKey Secret，请重新填写后点「保存配置」。');
      } else {
        setSecretHint('');
      }
    }
    if (r.media_storage) {
      setMedia(r.media_storage);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const r = await apiFetch<OssConfigResp>('get_user_media_oss_config', { method: 'GET' });
      applyServerOssState(r);
    } catch {
      setErr('加载失败');
    } finally {
      setLoading(false);
    }
  }, [applyServerOssState]);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveConfig() {
    setErr('');
    setMsg('');
    setSecretHint('');
    const secretJustEntered = (form.access_key_secret ?? '').trim();
    try {
      const r = await apiFetch<OssConfigResp>('save_user_media_oss_config', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      applyServerOssState(r, secretJustEntered);
      const shown = (r.config?.access_key_secret ?? secretJustEntered).trim();
      if (shown) {
        setMsg('配置已保存，下方 AccessKey Secret 为当前生效值，便于核对。');
      } else {
        setMsg('配置已保存');
        setSecretHint('保存后未读到 Secret，请确认已部署最新服务端（dist/api），并重新填写 Secret 再保存。');
      }
    } catch {
      setErr('保存失败');
    }
  }

  async function testConnection() {
    setTesting(true);
    setErr('');
    setMsg('');
    try {
      const r = await apiFetch<{
        ok: boolean;
        test_url?: string;
        err?: string;
        message?: string;
        detail?: OssTestFailureDetail;
      }>('test_user_media_oss', {
        method: 'POST',
        body: JSON.stringify({ ...form, save_before_test: true }),
      });
      if (r.ok) {
        setMsg(r.test_url ? `连接成功：${r.test_url}` : '连接测试成功');
        await load();
      } else {
        setErr(formatOssTestFailure(r.err, r.detail, r.message));
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : '测试失败，请检查网络或登录状态');
    } finally {
      setTesting(false);
    }
  }

  async function runMigration() {
    if (!window.confirm('将把当前账号下所有本地图片上传到您的 OSS，并更新数据库中的地址。是否继续？')) {
      return;
    }
    setMigrating(true);
    setErr('');
    setMsg('');
    setVerifyOk(false);
    try {
      const start = await apiFetch<{ ok: boolean; job_id?: number; total_files?: number }>(
        'start_user_media_migrate',
        { method: 'POST', body: '{}' }
      );
      const jid = start.job_id ?? 0;
      const total = start.total_files ?? 0;
      setJobId(jid);
      setProgress({ done: 0, total, status: 'running' });
      if (total === 0) {
        setMsg('没有需要迁移的本地文件');
        setMigrating(false);
        return;
      }
      let done = 0;
      while (done < total) {
        const batch = await apiFetch<{ ok: boolean; done_files?: number; status?: string }>(
          'batch_user_media_migrate',
          {
            method: 'POST',
            body: JSON.stringify({ job_id: jid, batch_size: 8 }),
          }
        );
        done = batch.done_files ?? done;
        setProgress({ done, total, status: batch.status ?? 'running' });
        if (batch.status === 'failed') {
          setErr('迁移中断，请查看服务端日志后重试');
          break;
        }
      }
      if (done >= total) {
        const ver = await apiFetch<{ ok: boolean; mismatches?: unknown[] }>('verify_user_media_migrate', {
          method: 'POST',
          body: JSON.stringify({ job_id: jid }),
        });
        setVerifyOk(ver.ok === true);
        setMsg(ver.ok ? '校验通过：远端文件与本地记录一致' : '校验未通过，请检查 mismatches 后重试');
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : '迁移失败');
    } finally {
      setMigrating(false);
      await load();
      void refreshSession();
    }
  }

  async function enableOssMode() {
    setErr('');
    try {
      await apiFetch('enable_user_oss_mode', {
        method: 'POST',
        body: JSON.stringify(verifyOk && jobId > 0 ? { job_id: jobId } : {}),
      });
      setMsg('已切换为 OSS 存储模式');
      await load();
      void refreshSession();
    } catch {
      setErr('切换失败：请先完成连接测试；若有本地文件须先迁移并校验');
    }
  }

  async function purgeLocal() {
    if (!verifyOk || jobId < 1) {
      setErr('请先完成迁移与校验');
      return;
    }
    if (!window.confirm('将删除本服务器上已迁移成功的本地图片文件，不可恢复。确定继续？')) {
      return;
    }
    setPurging(true);
    try {
      const r = await apiFetch<{ ok: boolean; deleted_files?: number }>('purge_user_local_media', {
        method: 'POST',
        body: JSON.stringify({ job_id: jobId }),
      });
      setMsg(`已删除 ${r.deleted_files ?? 0} 个本地文件`);
      await load();
      void refreshSession();
    } catch {
      setErr('清理失败');
    } finally {
      setPurging(false);
    }
  }

  const pct = media ? mediaUsagePercent(media.usage_bytes, media.quota_bytes) : 0;

  if (loading) {
    return <Spinner label="加载 OSS 配置" />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="OSS 参数设置"
        description="配置仅对当前登录账号生效。图片将存到您自有的阿里云 OSS，访问流量走 OSS 外网，减轻本服务器带宽与磁盘压力。Bucket 须为公共读（或公共读私有写）。"
        actions={
          <Button type="button" variant="secondary" onClick={() => openOssAliyunGuideWindow()}>
            <i className="fa-solid fa-book-open mr-1.5 text-xs opacity-80" aria-hidden />
            查看阿里云开通指引
          </Button>
        }
      />

      {media && !ossMode ? (
        <Card title="当前存储状态">
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">本地空间占用</span>
              <span className="font-semibold text-slate-900">
                {formatMediaBytes(media.usage_bytes)}
                {media.quota_bytes != null ? ` / ${formatMediaBytes(media.quota_bytes)}` : ''}
              </span>
            </div>
            {media.quota_bytes != null ? (
              <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    media.at_quota ? 'bg-rose-500' : media.near_quota ? 'bg-amber-500' : 'bg-teal-500'
                  )}
                  style={{ width: `${pct}%` }}
                />
              </div>
            ) : null}
            {media.at_quota ? (
              <p className="text-sm font-medium text-rose-600">已达限额，请完成下方配置并迁移后切换模式。</p>
            ) : media.near_quota ? (
              <p className="text-sm text-amber-700">已超过 80%，建议尽快迁移至 OSS。</p>
            ) : null}
          </div>
        </Card>
      ) : ossMode ? (
        <Card title="当前模式">
          <p className="text-sm text-emerald-700">已启用 OSS 存储，新上传将直传您的对象存储。</p>
        </Card>
      ) : null}

      <Card title="阿里云 OSS">
        <p className="mb-4 text-sm text-slate-600">
          填写 OSS Endpoint、Bucket、AccessKey。图片外链形如 https://oss-cn-xxx.aliyuncs.com/你的Bucket/wes/…
          ，请在微信小程序 download 合法域名中加入该 OSS 域名。
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <label className={labelClass}>
            <span>
              OSS Endpoint
              <FieldAliyunLink linkId="oss_endpoint_doc" text="查看地域 Endpoint" />
            </span>
            <input
              className={inputClass}
              placeholder="oss-cn-hangzhou.aliyuncs.com"
              value={form.endpoint ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, endpoint: e.target.value }))}
            />
          </label>
          <label className={labelClass}>
            <span>
              Bucket
              <FieldAliyunLink linkId="oss_bucket" text="创建 Bucket" />
            </span>
            <input
              className={inputClass}
              value={form.bucket ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, bucket: e.target.value }))}
            />
          </label>
          <label className={labelClass}>
            <span>
              AccessKey ID
              <FieldAliyunLink linkId="ram_ak" text="创建 AccessKey" />
            </span>
            <input
              className={inputClass}
              value={form.access_key_id ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, access_key_id: e.target.value }))}
              autoComplete="off"
            />
          </label>
          <label className={labelClass}>
            <span>
              AccessKey Secret
              <FieldAliyunLink linkId="ram_user" text="RAM 子账号（推荐）" />
            </span>
            <input
              type="text"
              name="wes-oss-access-key-secret"
              className={cn(inputClass, 'font-mono text-[13px]')}
              placeholder="RAM 用户的 AccessKey Secret（保存后明文显示，便于核对）"
              value={form.access_key_secret ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, access_key_secret: e.target.value }))}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              data-lpignore="true"
              data-1p-ignore="true"
            />
            {form.access_key_secret ? (
              <span className="mt-1 block text-xs text-emerald-700">
                已填写 {form.access_key_secret.length} 个字符，保存后仍显示在此供核对。
              </span>
            ) : (
              <span className="mt-1 block text-xs text-slate-500">仅当前登录账号可见，以明文保存在站点数据库中。</span>
            )}
            {secretHint ? <span className="mt-1 block text-xs font-medium text-amber-700">{secretHint}</span> : null}
          </label>
          <label className={labelClass}>
            <span>对象前缀（可选）</span>
            <input
              className={inputClass}
              value={form.object_prefix ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, object_prefix: e.target.value }))}
            />
          </label>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={() => void saveConfig()}>
            保存配置
          </Button>
          <Button type="button" variant="primary" disabled={testing} onClick={() => void testConnection()}>
            {testing ? '测试中…' : '测试连接'}
          </Button>
          {form.last_test_ok ? (
            <span className="self-center text-xs font-medium text-emerald-600">上次测试已通过</span>
          ) : null}
        </div>
      </Card>

      <Card title="迁移与切换">
        <ol className="mb-4 list-decimal space-y-1 pl-5 text-sm text-slate-600">
          <li>保存并测试 OSS 连接；</li>
          <li>一键上传：将本地图片批量传到 OSS 并改写数据库地址；</li>
          <li>系统自动校验远端文件；</li>
          <li>切换为 OSS 模式后，可删除服务器上的旧文件释放空间。</li>
        </ol>
        {migrating ? (
          <div className="mb-4 rounded-xl border border-teal-200 bg-teal-50/60 p-4">
            <p className="text-sm font-medium text-teal-900">
              上传进度：{progress.done} / {progress.total}
            </p>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-white">
              <div
                className="h-full bg-teal-500 transition-all"
                style={{
                  width: progress.total > 0 ? `${Math.round((progress.done / progress.total) * 100)}%` : '0%',
                }}
              />
            </div>
          </div>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="primary" disabled={migrating || !form.last_test_ok} onClick={() => void runMigration()}>
            {migrating ? '迁移中…' : '一键上传多媒体资源'}
          </Button>
          <Button type="button" variant="secondary" disabled={!verifyOk || ossMode} onClick={() => void enableOssMode()}>
            切换为 OSS 模式
          </Button>
          <Button type="button" variant="secondary" disabled={purging || !ossMode} onClick={() => void purgeLocal()}>
            {purging ? '清理中…' : '删除服务器本地副本'}
          </Button>
        </div>
      </Card>

      {msg ? <p className={inlineSuccessClass}>{msg}</p> : null}
      {err ? (
        <p className={cn(inlineErrorClass, 'whitespace-pre-wrap font-normal leading-relaxed')}>{err}</p>
      ) : null}
    </div>
  );
}
